// OfferTableCell.tsx
import * as React from 'react';
import { connect } from 'react-redux';
import { ApplicationState } from '../store';
import * as OfferStore from '../store/Offer';
import ItemOffer from "./ItemOffer";
import { createSelector } from 'reselect';
import { canUpdate } from '../security/roles';

interface OfferTableCellOwnProps {
    productId: number;  // ← number au lieu de string
    promotionId: number;
}

interface OfferTableCellConnectedProps {
    product: any;
    itemOffer: any;
    currency: any;
    priceNames: any;
    itemOfferState: any;
    disabled: boolean;
    promotionDate: any;
    promotionDefaultPriceNameId: any;
    isNewDateSelected: boolean;
}

interface OfferTableCellDispatchProps {
    requestUpdateItemPromotion: (timestamp: number, storeItemId: any, promotionId: any) => void;
    requestCreateItemPromotion: (timestamp: number, storeItemId: any, promotionId: any, priceNameId: any, date: any) => void;
    requestDeleteItemOffer: (timestamp: number, storeItemId: any, promotionId: any) => void;
    resetItemOfferState: (storeItemId: any, promotionId: any) => void;
    updateDateEndItemOffer: (storeItemId: any, promotionId: any, date: any) => void;
    updateDaysBeforeExpirityItemOffer: (storeItemId: any, promotionId: any, value: any) => void;
    updateCheckedItemOffer: (storeItemId: any, promotionId: any, value: boolean) => void;
    updatePriceNameIdItemOffer: (storeItemId: any, promotionId: any, value: any) => void;
}

type OfferTableCellProps = OfferTableCellOwnProps & OfferTableCellConnectedProps & OfferTableCellDispatchProps;

const OfferTableCellComponent: React.FC<OfferTableCellProps> = React.memo(({ 
    product, 
    itemOffer, 
    currency, 
    priceNames,
    itemOfferState,
    disabled,
    promotionDate,
    promotionDefaultPriceNameId,
    isNewDateSelected,
    productId,
    promotionId,
    requestUpdateItemPromotion,
    requestCreateItemPromotion,
    requestDeleteItemOffer,
    resetItemOfferState,
    updateDateEndItemOffer,
    updateDaysBeforeExpirityItemOffer,
    updateCheckedItemOffer,
    updatePriceNameIdItemOffer
}) => {
    if (!product) return null;
    
    const handleValidate = React.useCallback(() => {
        const timestamp = new Date().getTime();
        
        if (itemOffer) {
            requestUpdateItemPromotion(timestamp, productId, promotionId);
        } else {
            const hasDefaultPrice = product.prices?.some(y => 
                y.priceNameId === promotionDefaultPriceNameId
            );
            
            requestCreateItemPromotion(
                timestamp,
                productId,
                promotionId,
                hasDefaultPrice ? promotionDefaultPriceNameId : undefined,
                promotionDate
            );
        }
        
        if (promotionDate && isNewDateSelected) {
            updateDateEndItemOffer(productId, promotionId, promotionDate);
        }
    }, [itemOffer, product, productId, promotionId, promotionDefaultPriceNameId, 
        promotionDate, isNewDateSelected, requestUpdateItemPromotion, 
        requestCreateItemPromotion, updateDateEndItemOffer]);
    
    const handleDelete = React.useCallback(() => {
        if (itemOffer) {
            resetItemOfferState(productId, 0);
            requestDeleteItemOffer(new Date().getTime(), productId, promotionId);
        } else {
            resetItemOfferState(productId, promotionId);
        }
    }, [itemOffer, productId, promotionId, resetItemOfferState, requestDeleteItemOffer]);
    
    return (
        <ItemOffer 
            currency={currency}
            itemOfferState={itemOfferState}
            disabled={disabled}
            priceNames={priceNames}
            prices={product.prices}
            defaultPriceNameId={promotionDefaultPriceNameId}
            itemOffer={itemOffer ? {
                dateEnd: itemOffer.dateEnd,
                priceNameId: itemOffer.priceNameId,
            } : undefined}
            defaultDateEnd={promotionDate}
            hasDateEnd={true}
            hasDaysBeforeExpirity={false}
            onValidate={handleValidate}
            onDelete={handleDelete}
            updateDaysBeforeExpirity={(value) => 
                updateDaysBeforeExpirityItemOffer(productId, promotionId, value)
            }
            updateDateEnd={(value) => 
                updateDateEndItemOffer(productId, promotionId, value)
            }
            updateChecked={(value) => 
                updateCheckedItemOffer(productId, promotionId, value)
            }
            updatePriceNameId={(value) => 
                updatePriceNameIdItemOffer(productId, promotionId, value)
            } 
        />
    );
});

// Selectors
const getCurrencies = (state: ApplicationState) => state.seed.seed.currencies;
const getCurrencyId = (state: ApplicationState) => state.settings.storeSettings.currencyId;

const currencySelector = createSelector(
    [getCurrencies, getCurrencyId],
    (currencies, currencyId) => currencies[currencyId]
);

const mapStateToProps = (state: ApplicationState, ownProps: OfferTableCellOwnProps) => {
    const products = OfferStore.productPromoFilteredSelector(state);
    const product = products.find(p => p.storeItemId === ownProps.productId);
    const itemOffer = state.offer.itemOfferResult?.itemPromotions?.[ownProps.productId]
        ?.find(x => x.promotionId === ownProps.promotionId);
    
    return {
        product,
        itemOffer,
        currency: currencySelector(state),
        priceNames: state.seed.seed.priceNames,
        itemOfferState: state.offer.itemOfferStates[ownProps.productId]?.[ownProps.promotionId],
        disabled: !canUpdate(state),
        promotionDate: state.offer.promotionDate,
        promotionDefaultPriceNameId: state.offer.promotionDefaultPriceNameId[ownProps.promotionId],
        isNewDateSelected: state.offer.isNewDateSelected
    };
};

const mapDispatchToProps = {
    requestUpdateItemPromotion: OfferStore.actionCreators.requestUpdateItemPromotion,
    requestCreateItemPromotion: OfferStore.actionCreators.requestCreateItemPromotion,
    requestDeleteItemOffer: OfferStore.actionCreators.requestDeleteItemOffer,
    resetItemOfferState: OfferStore.actionCreators.resetItemOfferState,
    updateDateEndItemOffer: OfferStore.actionCreators.updateDateEndItemOffer,
    updateDaysBeforeExpirityItemOffer: OfferStore.actionCreators.updateDaysBeforeExpirityItemOffer,
    updateCheckedItemOffer: OfferStore.actionCreators.updateCheckedItemOffer,
    updatePriceNameIdItemOffer: OfferStore.actionCreators.updatePriceNameIdItemOffer
};

export const OfferTableCell = connect(mapStateToProps, mapDispatchToProps)(OfferTableCellComponent);